import { useAuthStore } from "../stores/authStore";
import type {
  StoredActivity,
  ActivityListItem,
  SyncResult,
  TimeRange,
  StatisticsData,
} from "@runlens/shared";

const API_BASE = "/api";

interface RequestOptions extends RequestInit {
  token?: string;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

// Module-level promise to prevent concurrent refresh attempts in API layer
let apiRefreshPromise: Promise<boolean> | null = null;

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { token, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData (let browser set it with boundary)
  if (!(fetchOptions.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new ApiError(response.status, error.error || "Request failed");
  }

  return response.json();
}

/**
 * Makes an authenticated request with automatic token refresh on 401.
 * Use this for all authenticated API calls to ensure session continuity.
 */
export async function authenticatedRequest<T>(
  endpoint: string,
  options: Omit<RequestOptions, "token"> = {}
): Promise<T> {
  const store = useAuthStore.getState();
  const { session } = store;

  if (!session?.access_token) {
    throw new ApiError(401, "Not authenticated");
  }

  try {
    return await request<T>(endpoint, { ...options, token: session.access_token });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      // Token expired - attempt refresh
      if (!session.refresh_token) {
        await store.logout();
        throw error;
      }

      // Prevent concurrent refresh attempts
      if (!apiRefreshPromise) {
        apiRefreshPromise = store.refreshSession().finally(() => {
          apiRefreshPromise = null;
        });
      }

      const refreshed = await apiRefreshPromise;

      if (refreshed) {
        // Retry with new token
        const newSession = useAuthStore.getState().session;
        if (newSession?.access_token) {
          return await request<T>(endpoint, {
            ...options,
            token: newSession.access_token,
          });
        }
      }

      // Refresh failed, redirect will happen via logout
      throw error;
    }

    throw error;
  }
}

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    request<{
      user: { id: string; email: string };
      session: { access_token: string; refresh_token: string; expires_at: number };
    }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  register: (email: string, password: string, confirmPassword: string) =>
    request<{
      user: { id: string; email: string } | null;
      session: { access_token: string; refresh_token: string; expires_at: number } | null;
      message?: string;
      requiresConfirmation: boolean;
    }>("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, confirmPassword }),
    }),

  logout: (token: string) =>
    request<{ success: boolean }>("/auth/logout", {
      method: "POST",
      token,
    }),

  session: (token: string) =>
    request<{
      user: { id: string; email: string } | null;
      session: { access_token: string } | null;
    }>("/auth/session", { token }),

  refresh: (refreshToken: string) =>
    request<{
      user: { id: string; email: string };
      session: { access_token: string; refresh_token: string; expires_at: number };
    }>("/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),

  forgotPassword: (email: string) =>
    request<{ success: boolean; message: string }>("/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  resetPassword: (
    accessToken: string,
    refreshToken: string | undefined,
    password: string
  ) =>
    request<{ success: boolean; message: string }>("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({
        access_token: accessToken,
        refresh_token: refreshToken,
        password,
      }),
    }),

  changePassword: (
    token: string,
    currentPassword: string,
    newPassword: string
  ) =>
    request<{ success: boolean; message: string }>("/auth/password", {
      method: "PUT",
      token,
      body: JSON.stringify({
        currentPassword,
        newPassword,
      }),
    }),
};

// Profile API
export interface Profile {
  id: string;
  name: string | null;
  avatarUrl: string | null;
  bio: string | null;
  onboardingCompleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export const profileApi = {
  get: (token: string) =>
    request<Profile>("/profile", { token }),

  update: (token: string, data: { name?: string; bio?: string }) =>
    request<Profile>("/profile", {
      method: "PUT",
      token,
      body: JSON.stringify(data),
    }),

  uploadAvatar: (token: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return request<{ avatarUrl: string; updatedAt: string }>("/profile/avatar", {
      method: "POST",
      token,
      body: formData,
    });
  },

  deleteAvatar: (token: string) =>
    request<{ success: boolean }>("/profile/avatar", {
      method: "DELETE",
      token,
    }),
};

// Integrations API
export type IntegrationStatus = "connected" | "disconnected" | "error";

// Insight generation status (from cron processor)
export type InsightStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "skipped";

export interface IntegrationStatusResponse {
  provider: string;
  status: IntegrationStatus;
  lastSyncAt: string | null;
  errorMessage: string | null;
}

export interface ActivitySummary {
  id: string;
  name: string;
  type: string;
  date: string;
  distance: number;
  duration: number;
  averagePace?: number;
  averageHR?: number;
  calories?: number;
}

export interface HRZone {
  zone: number;
  low: number;
  high: number;
  timeInZone: number;
}

export interface ActivityMetricSample {
  timestamp: number; // ms from activity start
  distance: number; // cumulative meters
  heartRate?: number; // bpm
  speed?: number; // m/s
  elevation?: number; // meters
}

export interface ActivityDetails extends ActivitySummary {
  aerobicTrainingEffect?: number;
  anaerobicTrainingEffect?: number;
  hrZones?: HRZone[];
  maxHR?: number;
  elevationGain?: number;
  elevationLoss?: number;
  samples?: ActivityMetricSample[];
}

export const integrationsApi = {
  status: (provider: string) =>
    authenticatedRequest<IntegrationStatusResponse>(
      `/integrations/${provider}/status`
    ),

  connect: (provider: string, email: string, password: string) =>
    authenticatedRequest<{ success: boolean; status: IntegrationStatus; error?: string }>(
      `/integrations/${provider}/connect`,
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }
    ),

  disconnect: (provider: string) =>
    authenticatedRequest<{ success: boolean; status: IntegrationStatus }>(
      `/integrations/${provider}/disconnect`,
      { method: "POST" }
    ),

  activities: (provider: string, options?: { limit?: number; start?: number }) => {
    const params = new URLSearchParams();
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.start) params.set("start", options.start.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return authenticatedRequest<{ activities: ActivitySummary[] }>(
      `/integrations/${provider}/activities${query}`
    );
  },

  activityDetails: (provider: string, activityId: string) =>
    authenticatedRequest<ActivityDetails>(
      `/integrations/${provider}/activities/${activityId}`
    ),

  /** Get OAuth authorization URL for providers that use OAuth flow */
  oauthUrl: (provider: string) =>
    authenticatedRequest<{ url: string }>(
      `/integrations/${provider}/oauth-url`
    ),
};

// Activities API (local storage)
export interface ActivitiesListResponse {
  activities: ActivityListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface UploadResponse {
  isDuplicate: false;
  activity: StoredActivity;
}

export interface DuplicateResponse {
  isDuplicate: true;
  existingActivity: ActivityListItem;
}

export const activitiesApi = {
  /** List user's locally stored activities */
  list: (options?: { limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (options?.limit) params.set("limit", options.limit.toString());
    if (options?.offset) params.set("offset", options.offset.toString());
    const query = params.toString() ? `?${params.toString()}` : "";
    return authenticatedRequest<ActivitiesListResponse>(`/activities${query}`);
  },

  /** Get activity details with samples */
  get: (activityId: string) =>
    authenticatedRequest<StoredActivity>(`/activities/${activityId}`),

  /** Upload a FIT file */
  upload: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return authenticatedRequest<UploadResponse | DuplicateResponse>(
      "/activities/upload",
      {
        method: "POST",
        body: formData,
      }
    );
  },

  /** Update activity properties */
  update: (activityId: string, data: { name?: string }) =>
    authenticatedRequest<StoredActivity>(`/activities/${activityId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  /** Delete an activity */
  delete: (activityId: string) =>
    authenticatedRequest<{ success: boolean }>(`/activities/${activityId}`, {
      method: "DELETE",
    }),

  /** Sync activities from a provider to local storage
   * @param provider - Provider name (e.g., "garmin")
   * @param options.full - If true, sync all activities (no early termination)
   * @param options.pageSize - Number of activities per page (1-50, default: 5)
   */
  sync: (provider: string, options?: { full?: boolean; pageSize?: number }) => {
    const params = new URLSearchParams();
    if (options?.full) params.set("full", "true");
    if (options?.pageSize) params.set("pageSize", String(options.pageSize));
    const query = params.toString();
    const url = `/activities/sync/${provider}${query ? `?${query}` : ""}`;
    return authenticatedRequest<SyncResult>(url, { method: "POST" });
  },
};

// Insights API
export interface TrainingAnalysis {
  summary: string;
  effortLevel: "easy" | "moderate" | "hard" | "very_hard";
  paceConsistency: string;
  heartRateAnalysis?: string;
  trainingZoneBreakdown?: string;
  strengths: string[];
  areasForImprovement: string[];
}

export interface CoachingSuggestions {
  forNextRun: string[];
  recoveryAdvice: string;
  weeklyRecommendations?: string[];
}

export interface SessionComparisonStats {
  category: string;
  confidence: number;
  comparedTo: number;
  trends: {
    pace?: { current: string; average: string; trend: string };
    heartRate?: { current: string; average: string; trend: string };
    distance?: { current: string; average: string; trend: string };
    trainingEffect?: { current: string; average: string; trend: string };
  };
  insight: string;
}

export interface ActivityInsightResponse {
  id: string;
  activityId: string;
  generatedAt: string;
  trainingAnalysis?: TrainingAnalysis;
  coachingSuggestions?: CoachingSuggestions;
  sessionComparison?: SessionComparisonStats;
  cached: boolean;
}

export const insightsApi = {
  get: (
    activityId: string,
    options?: { provider?: string; refresh?: boolean; cacheOnly?: boolean }
  ) => {
    const params = new URLSearchParams();
    if (options?.provider) params.set("provider", options.provider);
    if (options?.refresh) params.set("refresh", "true");
    if (options?.cacheOnly) params.set("cacheOnly", "true");
    const query = params.toString() ? `?${params.toString()}` : "";
    return authenticatedRequest<ActivityInsightResponse>(
      `/insights/${activityId}${query}`
    );
  },

  getCached: (activityId: string, provider?: string) =>
    insightsApi.get(activityId, { provider, cacheOnly: true }),

  refresh: (activityId: string, provider?: string) =>
    insightsApi.get(activityId, { provider, refresh: true }),

  delete: (activityId: string) =>
    authenticatedRequest<{ success: boolean }>(`/insights/${activityId}`, {
      method: "DELETE",
    }),
};

// Chat API
export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

export const chatApi = {
  getHistory: (activityId: string, provider?: string) => {
    const params = new URLSearchParams();
    if (provider) params.set("provider", provider);
    const query = params.toString() ? `?${params.toString()}` : "";
    return authenticatedRequest<{ messages: ChatMessage[] }>(
      `/chat/${activityId}${query}`
    );
  },

  sendMessage: (activityId: string, message: string, provider?: string) => {
    const params = new URLSearchParams();
    if (provider) params.set("provider", provider);
    const query = params.toString() ? `?${params.toString()}` : "";
    return authenticatedRequest<{
      userMessage: ChatMessage;
      assistantMessage: ChatMessage;
    }>(`/chat/${activityId}${query}`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  },

  clearHistory: (activityId: string) =>
    authenticatedRequest<{ success: boolean }>(`/chat/${activityId}`, {
      method: "DELETE",
    }),
};

// Global Chat API (not tied to specific activity, grouped by daily sessions)
export const globalChatApi = {
  getSessions: () =>
    authenticatedRequest<{ sessions: string[] }>("/chat/global/sessions"),

  getHistory: (date?: string) => {
    const query = date ? `?date=${date}` : "";
    return authenticatedRequest<{ messages: ChatMessage[] }>(
      `/chat/global${query}`
    );
  },

  sendMessage: (message: string) =>
    authenticatedRequest<{
      userMessage: ChatMessage;
      assistantMessage: ChatMessage;
    }>("/chat/global", {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  clearHistory: (date?: string) => {
    const query = date ? `?date=${date}` : "";
    return authenticatedRequest<{ success: boolean }>(`/chat/global${query}`, {
      method: "DELETE",
    });
  },
};

// Statistics API
export const statisticsApi = {
  get: (range: TimeRange = "1y") =>
    authenticatedRequest<StatisticsData>(`/statistics?range=${range}`),
};


// Training Plans API
import type {
  TrainingPlan,
  TrainingPlanWithSessions,
  TrainingPlanWithMetrics,
  PlannedSession,
  SessionChange,
  RunnerProfile,
  PlanSuggestion,
  MetricSuggestion,
  GoalMetric,
  MetricDataSource,
  MetricDirection,
  MetricSourceConfig,
  MetricTargetValue,
  MetricType,
  MetricUnit,
  PlanStatus,
  GoalType,
  GoalEvent,
} from "@runlens/shared";

export const trainingPlansApi = {
  /** List user's training plans */
  list: (status?: PlanStatus) => {
    const query = status ? `?status=${status}` : "";
    return authenticatedRequest<{ plans: TrainingPlan[] }>(
      `/training-plans${query}`
    );
  },

  /** Get a training plan with sessions and metrics */
  get: (planId: string, weeks?: number) => {
    const query = weeks ? `?weeks=${weeks}` : "";
    return authenticatedRequest<TrainingPlanWithMetrics>(
      `/training-plans/${planId}${query}`
    );
  },

  /** Create a new training plan */
  create: (data: {
    name: string;
    goalType?: GoalType;
    goalEvent?: GoalEvent;
    goalDate?: string;
  }) =>
    authenticatedRequest<TrainingPlan>("/training-plans", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Update a training plan */
  update: (
    planId: string,
    data: {
      name?: string;
      status?: PlanStatus;
      goalType?: GoalType;
      goalEvent?: GoalEvent;
      goalDate?: string | null;
    }
  ) =>
    authenticatedRequest<TrainingPlan>(`/training-plans/${planId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /** Archive a training plan */
  delete: (planId: string) =>
    authenticatedRequest<{ success: boolean }>(`/training-plans/${planId}`, {
      method: "DELETE",
    }),

  /** Batch update sessions */
  updateSessions: (planId: string, changes: SessionChange[]) =>
    authenticatedRequest<{ added: number; updated: number; deleted: number }>(
      `/training-plans/${planId}/sessions`,
      {
        method: "PUT",
        body: JSON.stringify({ changes }),
      }
    ),

  /** Trigger activity matching */
  match: (planId: string) =>
    authenticatedRequest<{
      autoMatched: number;
      needsReview: number;
      matches: Array<{
        sessionId: string;
        activityId: string;
        confidence: "high" | "medium" | "low";
        reason: string;
      }>;
      unmatched: Array<{
        id: string;
        scheduledDate: string;
        sessionType: string;
        daysOverdue: number;
      }>;
    }>(`/training-plans/${planId}/match`, {
      method: "POST",
    }),

  /** Mark a session as skipped */
  skipSession: (planId: string, sessionId: string) =>
    authenticatedRequest<{ success: boolean }>(
      `/training-plans/${planId}/sessions/${sessionId}/skip`,
      { method: "POST" }
    ),

  /** Mark a session as missed */
  missSession: (planId: string, sessionId: string) =>
    authenticatedRequest<{ success: boolean }>(
      `/training-plans/${planId}/sessions/${sessionId}/miss`,
      { method: "POST" }
    ),

  /** Manually match a session to an activity */
  matchSession: (planId: string, sessionId: string, activityId: string) =>
    authenticatedRequest<{ success: boolean }>(
      `/training-plans/${planId}/sessions/${sessionId}/match`,
      {
        method: "POST",
        body: JSON.stringify({ activityId }),
      }
    ),

  /** Reset a plan - delete all sessions and clear coach history */
  reset: (planId: string) =>
    authenticatedRequest<{ success: boolean }>(
      `/training-plans/${planId}/reset`,
      { method: "POST" }
    ),

  /** Get computed goal metrics for a plan */
  getMetrics: (planId: string) =>
    authenticatedRequest<{ metrics: GoalMetric[] }>(
      `/training-plans/${planId}/metrics`
    ),

  /** Create a goal metric */
  createMetric: (
    planId: string,
    data: {
      name: string;
      metricType: MetricType;
      dataSource: MetricDataSource;
      sourceConfig: MetricSourceConfig;
      targetValue?: MetricTargetValue;
      unit: MetricUnit;
      direction: MetricDirection;
      displayOrder?: number;
    }
  ) =>
    authenticatedRequest<GoalMetric>(`/training-plans/${planId}/metrics`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  /** Delete a goal metric */
  deleteMetric: (planId: string, metricId: string) =>
    authenticatedRequest<{ success: boolean }>(
      `/training-plans/${planId}/metrics/${metricId}`,
      { method: "DELETE" }
    ),
};

// Coach API (grouped by daily sessions within each plan)
export const coachApi = {
  /** List available session dates for a plan */
  getSessions: (planId: string) =>
    authenticatedRequest<{ sessions: string[] }>(`/coach/${planId}/sessions`),

  /** Get coach chat history for a plan (optionally for a specific date) */
  getHistory: (planId: string, date?: string) => {
    const query = date ? `?date=${date}` : "";
    return authenticatedRequest<{
      messages: ChatMessage[];
      pendingSuggestion?: PlanSuggestion | null;
      readyForSuggestion?: boolean;
      pendingMetricSuggestion?: MetricSuggestion | null;
      readyForMetricSuggestion?: boolean;
    }>(`/coach/${planId}${query}`);
  },

  /** Send a message to the coach */
  sendMessage: (planId: string, message: string) =>
    authenticatedRequest<{
      userMessage: ChatMessage;
      assistantMessage: ChatMessage;
      displayText: string;
      suggestion?: PlanSuggestion;
      readyForSuggestion?: boolean;
    }>(`/coach/${planId}`, {
      method: "POST",
      body: JSON.stringify({ message }),
    }),

  /** Start a discovery conversation */
  start: (planId: string) =>
    authenticatedRequest<{
      message: string;
      assistantMessage: ChatMessage;
    }>(`/coach/${planId}/start`, {
      method: "POST",
    }),

  /** Clear coach chat history (optionally for a specific date) */
  clearHistory: (planId: string, date?: string) => {
    const query = date ? `?date=${date}` : "";
    return authenticatedRequest<{ success: boolean }>(`/coach/${planId}${query}`, {
      method: "DELETE",
    });
  },

  /** Mark a suggestion as accepted or declined */
  processSuggestion: (planId: string, action: "accept" | "decline") =>
    authenticatedRequest<{ success: boolean }>(`/coach/${planId}/suggestion`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),

  /** Accept or decline a metric suggestion */
  processMetricSuggestion: (
    planId: string,
    action: "accept" | "decline",
    metrics?: MetricSuggestion["metrics"]
  ) =>
    authenticatedRequest<{ success: boolean }>(
      `/coach/${planId}/metric-suggestion`,
      {
        method: "POST",
        body: JSON.stringify({ action, metrics }),
      }
    ),
};

// Runner Profile API
export const runnerProfileApi = {
  /** Get runner profile (creates if doesn't exist) */
  get: () => authenticatedRequest<RunnerProfile>("/runner-profile"),

  /** Update runner profile */
  update: (data: Partial<Omit<RunnerProfile, "userId" | "createdAt" | "updatedAt">>) =>
    authenticatedRequest<RunnerProfile>("/runner-profile", {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  /** Add a learned fact */
  addFact: (fact: string) =>
    authenticatedRequest<RunnerProfile>("/runner-profile/facts", {
      method: "POST",
      body: JSON.stringify({ fact }),
    }),

  /** Remove a learned fact */
  removeFact: (index: number) =>
    authenticatedRequest<RunnerProfile>(`/runner-profile/facts/${index}`, {
      method: "DELETE",
    }),
};

// Onboarding API
export const onboardingApi = {
  getStatus: () =>
    authenticatedRequest<{ required: boolean }>("/onboarding/status"),

  complete: () =>
    authenticatedRequest<{ success: boolean }>("/onboarding/complete", {
      method: "POST",
    }),
};

export { ApiError };
